import React, { useState, useEffect } from 'react';
import axios from 'axios';

import { Intent, Alignment } from '@blueprintjs/core';
import { NotificationToaster, AlertUnsavedData } from "components/notifications";
import { ButtonSave } from "components/buttons";
import { AccountService, UserService } from "services";
import { useDirtyData } from "hooks/useDirtyData";
import { ShowHide } from 'components/layout';
import { PageRestricted } from 'pages/errors/page-restricted';
import { FormSwitch } from 'components/form-fields';
import { UserHasAccess } from 'components/user-access';

export function UserAccountSwitching(props) {
    const requiredAction = 'Internal';
    const hasRequiredAction = UserHasAccess(requiredAction);
    const internalAccountId = AccountService.internalAccountId();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [originalValues, setOriginalValues] = useState({ canSwitchAccounts: false });
    const [canSwitchAccounts, setCanSwitchAccounts] = useState(false);
    const [readOnly, setReadOnly] = useState(false);
    const [isCurrentInternalAccount, setIsCurrentInternalAccount] = useState(false);
    const isDataDirty = useDirtyData(originalValues, { canSwitchAccounts });

    useEffect(initialLoad, [props.user]);

    function initialLoad() {
        if (props.user == null || props.user.userId == null) {
            return;
        }

        axios.all([UserService.getAccountAccess(props.user.userId), UserService.getLoggedInUser()])
            .then(axios.spread(function (userAccountAccess, userDetails) {
                setReadOnly(props.user.userId === userDetails.userId);
                setLoading(false);
                setIsCurrentInternalAccount(userDetails.accountId === internalAccountId);
                setCanSwitchAccounts(userAccountAccess.canSwitchAccount);
                setOriginalValues({ canSwitchAccounts: userAccountAccess.canSwitchAccount });
            }), () => {
                NotificationToaster.show(Intent.DANGER, "Could not load settings. Please refresh the page.");
            });
    }

    function onCanSwitchChange(event) {
        setCanSwitchAccounts(event.target.checked);
    }

    function saveAccountAccess() {
        setSaving(true);
        UserService.updateAccountAccessSettingsInternal(props.user.userId, { canSwitchAccounts })
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, "Account access settings updated successfully.");
                setOriginalValues({ canSwitchAccounts });
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not update account access settings. Please try again.");
            })
            .finally(() => {
                setSaving(false);
            });
    }

    return (
        <ShowHide
            evaluator={hasRequiredAction && (loading || (isCurrentInternalAccount && props.user?.simpleAccountSwitching))}
            show={
                <div className="row">
                    <h2>Switch Accounts</h2>
                    <FormSwitch
                        disabled={saving || readOnly}
                        loading={loading}
                        headingText="Allow user to switch between accounts:"
                        checked={canSwitchAccounts}
                        alignIndicator={Alignment.START}
                        onChange={onCanSwitchChange}
                    />

                    <AlertUnsavedData
                        isDirty={isDataDirty}
                    />
                    <ShowHide
                        evaluator={!readOnly}
                        show={(
                            <ButtonSave
                                onClick={saveAccountAccess}
                                disabled={saving}
                                simpleDisabled={!isDataDirty}
                                loading={loading}
                            />
                        )}
                    />
                </div>
            }
            hide={
                <PageRestricted />
            }
        />
    );
}